import React from 'react';
import type { Transaction } from './lib/api-types';
import { pcg2026 } from './lib/pcg2026';

const pcgMap = new Map(pcg2026.map(a => [a.numero, a.libelle]));

interface OperationViewModalProps {
  operation: Transaction;
  onClose: () => void;
}

const OperationViewModal: React.FC<OperationViewModalProps> = ({ operation, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails de l'opération</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(operation.date).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Statut</label>
                <p className="mt-1 text-sm text-gray-900">
                  {operation.statut_validation === 'validated' ? 'Validée' : 'Brouillon'}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Libellé</label>
              <p className="mt-1 text-sm text-gray-900">{operation.libelle}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Lignes d'écriture</label>
              {operation.lignes && operation.lignes.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Compte PCG</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Débit (€)</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Crédit (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operation.lignes.map((line, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <span className="font-medium">{line.compte_id}</span>
                            {pcgMap.get(line.compte_id) && (
                              <span className="text-gray-500 ml-1">— {pcgMap.get(line.compte_id)}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            €{(line.debit_centimes / 100).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            €{(line.credit_centimes / 100).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500">
                  Aucune ligne d'écriture trouvée pour cette opération.
                  <br />
                  <small>Cette opération a peut-être été créée avant l'implémentation des lignes détaillées.</small>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperationViewModal;